import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { Autocomplete, Box, Button, Card, FormControl, FormControlLabel, FormLabel, Grid, Radio, RadioGroup, TextField } from "@mui/material";
import { getObjectListByPropertyApi, postSaveObjectApi, putUpdateObjectApi } from "api/general";
import { calculateNewDecisionNo, canonicalizeDepartmentKey, stripHTMLTags, updateSortedAgendaList } from 'app/utils/appHelpers';
import { DECISION, DECISION_FILE } from "app/utils/constants/customConstant";
import config, { ykUnitId } from 'config';
import { useEffect, useState } from "react";
import LoadingPopUp from '../LoadingPopup';
import { useDispatch, useSelector } from "react-redux";
import { setCurrentAgenda, showAgendaSnackbar } from 'stores/meeting';
import AgendaFileDialog from '../Dialog/AgendaFileDialog';
import AgendaTemplateSelectionDialog from '../Dialog/AgendaTemplateSelectionDialog';

const isExistingAgenda = (agendaItem) =>
    Boolean(agendaItem && !Array.isArray(agendaItem) && agendaItem.id);

function AddUpdateAgendaCard({ onClose }) {
    const dispatch = useDispatch();
    const authUser = useSelector(state => state.auth.authUser);
    const currentMeeting = useSelector((state) => state.meeting.currentMeeting);
    const currentAgenda = useSelector((state) => state.meeting.currentAgenda);
    const subjectList = useSelector((state) => state.subject.data) || [];

    const availableSubjectList = currentMeeting?.unitId === ykUnitId
        ? subjectList.filter((subject) => subject.isVisibleYk === 1)
        : subjectList;

    const currentAgendaList = useSelector((state) => state.meeting.currentAgendaList);
    const departmentList = config.departments;
    const [decisionNoTouched, setDecisionNoTouched] = useState(() => isExistingAgenda(currentAgenda));

    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [templateType, setTemplateType] = useState('agenda');
    const [fileDialogStatus, setFileDialogStatus] = useState(false);
    const [busy, setBusy] = useState(false);
    const [agendaFileList, setAgendaFileList] = useState([]);
    const { data: userList } = useSelector(state => state.user);

    const emptyAgenda = {
        adminUserId: authUser.id,
        meetingId: currentMeeting.id,
        principle: "Hayır",
        supp: "Hayır",
        department: '',
        secret: "Hayır",
        status: 0,
        yaysisMeetingId: 0,
        yaysisBoardId: 0,
        yaysisPubId: 0,
        saysMeetingId: 0,
        saysItemId: 0,
        pdate: new Date()
    };

    const [agenda, setAgenda] = useState(() =>
        isExistingAgenda(currentAgenda) ? currentAgenda : emptyAgenda
    );

    const isUpdateMode = isExistingAgenda(agenda);
    const type = isUpdateMode ? 'update' : 'add';

    const [agendaError, setAgendaError] = useState({
        subjectId: "",
        decisionNo: "",
        agenda: ""
    });

    useEffect(() => {
        if (type === "add" && (!agenda.department || agenda.department.trim() === "") && currentMeeting?.inst) {
            setAgenda((prev) => ({ ...prev, department: currentMeeting.inst }));
        }
    }, [type, currentMeeting, agenda.department]);

    const resolveDecisionNo = (item) => {
        if (item.supp === "Evet") {
            const extras = currentAgendaList.filter((a) => a.supp === "Evet" && a.id !== item.id);
            if (extras.length > 0) {
                return Math.max(...extras.map((a) => Number(a.decisionNo)), 0) + 1;
            }
            return currentAgendaList.length + 1;
        }
        const itemKey = canonicalizeDepartmentKey(item.department);
        const filtered = currentAgendaList.filter((a) =>
            String(a.subjectId) === String(item.subjectId) &&
            canonicalizeDepartmentKey(a.department) === itemKey &&
            a.supp !== "Evet" &&
            a.id !== item.id
        );
        return calculateNewDecisionNo(currentAgendaList, filtered, item.department);
    };

    const shouldAutoFillNumber = (item, { force = false } = {}) => {
        if (!force && decisionNoTouched) {
            return false;
        }
        if (!item.subjectId) {
            return false;
        }
        if (item.supp === "Evet") {
            return true;
        }
        if (!item.department || String(item.department).trim() === "") {
            return false;
        }
        return true;
    };

    const withAutoDecisionNo = (item, { force = false } = {}) => {
        if (!shouldAutoFillNumber(item, { force })) {
            if (force || !decisionNoTouched) {
                return { ...item, decisionNo: "" };
            }
            return item;
        }
        return { ...item, decisionNo: resolveDecisionNo(item) };
    };

    const handleTemplateSelect = (templateContent) => {
        if (templateType === 'agenda') {
            setAgenda(prev => {
                const currentAgenda = stripHTMLTags(prev.agenda || '');
                const newAgenda = currentAgenda.trim() !== ''
                    ? currentAgenda + '\n\n' + templateContent
                    : templateContent;
                return { ...prev, agenda: newAgenda };
            });
            setAgendaError(prev => ({ ...prev, agenda: "" }));
        } else {
            setAgenda(prev => {
                const currentDecision = stripHTMLTags(prev.decision || '');
                const newDecision = currentDecision.trim() !== ''
                    ? currentDecision + '\n\n' + templateContent
                    : templateContent;
                return { ...prev, decision: newDecision };
            });
        }
    };

    const openTemplateDialog = (type) => {
        setTemplateType(type);
        setTemplateDialogOpen(true);
    };

    const handleFileButtonClick = () => {
        if (fileDialogStatus) return;
        setFileDialogStatus(true);
        const fetchFiles = async () => {
            const res = await getObjectListByPropertyApi(DECISION_FILE, "decisionId", currentAgenda.id);
            const files = res.data || [];
            files.forEach(file => {
                file.user = userList.find(u => u.id === file.adminUserId);
            });
            setAgendaFileList(files);
        };
        fetchFiles();
    };

    const handleSave = async () => {

        const requiredFields = decisionNoTouched
            ? ["subjectId", "decisionNo", "agenda", "department"]
            : ["subjectId", "agenda", "department"];
        let errors = {};
        requiredFields.forEach(field => {
            if (!agenda[field] || (typeof agenda[field] === "string" && agenda[field].trim() === "")) {
                errors[field] = "Bu alan zorunludur";
            }
        });
        if (Object.keys(errors).length > 0) {
            setAgendaError(errors);
            return;
        }

        setBusy(true);
        try {
        if (type === "update") {

            const updatedAgenda = {
                ...agenda,
                pdate: new Date(),
                adminUserId: authUser.id
            };
            let agendaWithoutUser = { ...updatedAgenda };
            delete agendaWithoutUser.user;

            const result = await putUpdateObjectApi(DECISION, agendaWithoutUser);

            if (result.status) {

                const placementChanged =
                    currentAgenda?.supp !== updatedAgenda.supp ||
                    currentAgenda?.subjectId !== updatedAgenda.subjectId ||
                    currentAgenda?.department !== updatedAgenda.department;

                // Konu/bölüm değiştiyse ve numara elle yazılmadıysa kurallara göre yerleştir
                const updateType = (placementChanged && !decisionNoTouched) ? "update" : "updateV2";

                const savedList = await dispatch(await updateSortedAgendaList(
                    currentAgendaList,
                    updateType,
                    { ...updatedAgenda },
                    true
                ));
                const saved = Array.isArray(savedList)
                    ? savedList.find((item) => item.id === updatedAgenda.id)
                    : null;

                dispatch(showAgendaSnackbar({
                    message: `${saved?.decisionNo ?? updatedAgenda.decisionNo} numaralı gündemde değişiklik yapıldı.`,
                    severity: 'info'
                }));

            }
        } else {

            let newAgenda = { ...agenda };
            if (!newAgenda.decision || (typeof newAgenda.decision === "string" && newAgenda.decision.trim() === "")) {
                newAgenda.decision = newAgenda.agenda;
            }
            if (!decisionNoTouched) {
                newAgenda.decisionNo = 0;
            }
            const result = await postSaveObjectApi(DECISION, newAgenda);

            if (result.status) {

                const newAgendaWithUser = { id: result.data.insertId, ...newAgenda, user: authUser }

                const savedList = await dispatch(await updateSortedAgendaList(currentAgendaList, "change", newAgendaWithUser));
                const saved = Array.isArray(savedList)
                    ? savedList.find((item) => item.id === newAgendaWithUser.id)
                    : null;
                const assignedNo = saved?.decisionNo ?? newAgenda.decisionNo;

                dispatch(setCurrentAgenda({ ...newAgendaWithUser, decisionNo: assignedNo }));
                setAgenda({ ...newAgendaWithUser, decisionNo: assignedNo });

                dispatch(showAgendaSnackbar({
                    message: `${assignedNo} numaralı gündem eklendi.`,
                    severity: 'success'
                }));
            }
        }
        if (type === "update") {
            onClose();
        }
        } finally {
            setBusy(false);
        }
    };


    const handleChange = name => (e) => {

        const value = e.target.value;
        if (name === "decisionNo") {
            setDecisionNoTouched(true);
        }
        if (name === "supp") {
            setDecisionNoTouched(false);
            setAgenda((prev) => withAutoDecisionNo({ ...prev, [name]: value }, { force: true }));
            setAgendaError((prev) => ({ ...prev, [name]: "" }));
            return;
        }
        setAgenda({ ...agenda, [name]: value });
        setAgendaError({
            ...agendaError,
            [name]: ""
        });
    };


    const handleSubjectChange = (newSubjectId) => {
        setDecisionNoTouched(false);
        setAgenda((prev) => withAutoDecisionNo({
            ...prev,
            subjectId: newSubjectId
        }, { force: true }));
        setAgendaError(prev => ({ ...prev, subjectId: "" }));
    };



    const handleDepartmentChange = (newDepartment) => {
        const newDept = newDepartment.map(item => item.name).join(',');
        setDecisionNoTouched(false);
        setAgenda((prev) => withAutoDecisionNo({
            ...prev,
            department: newDept
        }, { force: true }));
        setAgendaError(prev => ({ ...prev, department: "" }));
    };

    const disabled = type === "preview";
    return (
        <>
            <LoadingPopUp open={busy} blocking message="" />
            <JumboCardQuick>
                <Grid container spacing={1} rowSpacing={1}>

                    <Grid item xs={3} md={3} lg={3}>
                        <FormControl fullWidth>
                            <Autocomplete
                                required
                                onChange={(event, newValue) => {
                                    handleSubjectChange(newValue ? newValue.id : "");
                                }}
                                name="subjectId"
                                label="Konu"
                                placeholder="Konu"
                                value={availableSubjectList.find(option => option.id === agenda.subjectId) || null}
                                getOptionLabel={(option) => option.name}
                                options={availableSubjectList}
                                renderInput={(params2) => (
                                    <TextField
                                        {...params2}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                        required
                                        placeholder="Konu"
                                        label="Konu"
                                        error={Boolean(agendaError.subjectId)}
                                        helperText={agendaError.subjectId}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>

                    {/* Bölüm (department) alanı - Autocomplete ile çoklu seçim */}
                    <Grid item xs={3} md={3} lg={3}>
                        <FormControl fullWidth>

                            <Autocomplete
                                multiple
                                required
                                options={departmentList}
                                getOptionLabel={(option) => option.name}
                                onChange={(event, newValue) => {
                                    handleDepartmentChange(newValue);
                                }}
                                value={
                                    agenda.department && agenda.department.trim() !== ""
                                        ? departmentList.filter(item =>
                                            agenda.department.split(',').map(s => s.trim()).includes(item.name)
                                        )
                                        : currentMeeting && currentMeeting.inst
                                            ? departmentList.filter(item => item.name === currentMeeting.inst)
                                            : []
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Bölüm"
                                        placeholder="Bölüm seçiniz"
                                        error={Boolean(agendaError.department)}
                                        helperText={agendaError.department}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                )}
                            />


                        </FormControl>
                    </Grid>

                    <Grid item xs={3} md={3} lg={2}>
                        <TextField
                            required
                            variant="outlined"
                            placeholder="Gündem No"
                            fullWidth
                            value={agenda.decisionNo}
                            type="number"
                            onChange={handleChange("decisionNo")}
                            InputLabelProps={{
                                shrink: true
                            }}
                            label="Gündem No"
                            disabled={disabled}
                            error={Boolean(agendaError.decisionNo)}
                            helperText={agendaError.decisionNo}
                        />
                    </Grid>

                    <Grid item xs={3} md={2} lg={2}>
                        <FormControl>
                            <FormLabel id="principle-label">İlke Kararı</FormLabel>
                            <RadioGroup
                                aria-labelledby="principle-label"
                                name="radio-buttons-group"
                                onChange={handleChange("principle")}
                                value={agenda.principle}
                                row
                                disabled={disabled}
                            >
                                <FormControlLabel value={"Hayır"} disabled={disabled} control={<Radio />} label="Hayır" />
                                <FormControlLabel value={"Evet"} disabled={disabled} control={<Radio />} label="Evet" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    <Grid item xs={3} md={2} lg={2}>
                        <FormControl>
                            <FormLabel id="supp-label">Ek Gündem</FormLabel>
                            <RadioGroup
                                aria-labelledby="supp-label"
                                name="radio-buttons-group"
                                onChange={handleChange("supp")}
                                value={agenda.supp}
                                row
                                disabled={disabled}
                            >
                                <FormControlLabel value={"Hayır"} disabled={disabled} control={<Radio />} label="Hayır" />
                                <FormControlLabel value={"Evet"} disabled={disabled} control={<Radio />} label="Evet" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {
                        currentMeeting.inst === "YK" ?
                            <>
                                <Grid item xs={6} md={6} lg={6}>
                                    <TextField
                                        variant="outlined"
                                        label="Yaysis Kayıt Numaraları"
                                        placeholder="Yaysis Kayıt Numaraları"
                                        multiline
                                        fullWidth
                                        value={agenda.yaysisPubIdList}
                                        onChange={handleChange("yaysisPubIdList")}
                                        InputLabelProps={{
                                            shrink: true
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={3} md={3} lg={3}>
                                    <FormControl>
                                        <FormLabel id="secret-label">Gizli Karar</FormLabel>
                                        <RadioGroup
                                            aria-labelledby="secret-label"
                                            name="radio-buttons-group"
                                            onChange={handleChange("secret")}
                                            value={agenda.secret}
                                            row
                                            disabled={disabled}
                                        >
                                            <FormControlLabel value={"Hayır"} disabled={disabled} control={<Radio />} label="Hayır" />
                                            <FormControlLabel value={"Evet"} disabled={disabled} control={<Radio />} label="Evet" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                            </> : ""
                    }

                    <Grid item xs={12} md={12} lg={12}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => openTemplateDialog('agenda')}
                            >
                                Gündemi Şablondan Getir
                            </Button>
                        </Box>
                        <TextField
                            required
                            variant="outlined"
                            placeholder="Gündem"
                            multiline
                            fullWidth
                            value={stripHTMLTags(agenda.agenda)}
                            type="number"
                            rows={8}
                            onChange={handleChange("agenda")}
                            InputLabelProps={{
                                shrink: true
                            }}
                            label="Gündem"
                            error={Boolean(agendaError.agenda)}
                            helperText={agendaError.agenda}
                        />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => openTemplateDialog('decision')}
                            >
                                Kararı Şablondan Getir
                            </Button>
                        </Box>
                        <TextField
                            variant="outlined"
                            placeholder="Karar (Gündemden farklı ise giriniz. Karar gündem ile aynı ise, gündem karara otomatik kopyalanacaktır.)"
                            multiline
                            fullWidth

                            value={stripHTMLTags(agenda.decision)}
                            onChange={handleChange("decision")}
                            InputLabelProps={{
                                shrink: true
                            }}
                            label="Karar"
                            rows={8}
                            inputProps={{
                                style: { overflow: 'hidden' } // Prevent scroll
                            }}
                        />
                    </Grid>

                </Grid>
            </JumboCardQuick>
            <br />

            <Card>
                <Box
                    sx={{
                        py: 1,
                        px: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                >
                    {isUpdateMode && (
                        <Button variant="contained" onClick={handleFileButtonClick}>
                            Dosyalar
                        </Button>
                    )}
                    <Button variant="contained" color="success" onClick={() => handleSave()}>
                        {isUpdateMode ? 'Güncelle' : 'Kaydet'}
                    </Button>
                    <Button variant="contained" color="error" onClick={() => onClose()}>
                        Kapat
                    </Button>
                </Box>
            </Card>

            <AgendaTemplateSelectionDialog
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                onTemplateSelect={handleTemplateSelect}
                templateType={templateType}
            />

            {fileDialogStatus && (
                <AgendaFileDialog
                    agendaFileList={agendaFileList}
                    setAgendaFileList={setAgendaFileList}
                    onClose={() => setFileDialogStatus(false)}
                    open={fileDialogStatus}
                />
            )}
        </>
    );
}

const style = document.createElement('style');
style.innerHTML = `
    .swal-container-custom {
        z-index: 10000 !important;
    }
`;
document.head.appendChild(style);

export default AddUpdateAgendaCard;
